import type { ApiRequest, ApiResponse, BorgeeTransport } from '../../client-core/transport';

export interface WebTransportOptions {
  /** Development impersonation belongs to the Web host, never shared core. */
  getDevUserId?: () => string | null;
  isDevelopment?: boolean;
  baseUrl?: string;
}

/** Converts the data-only client contract into browser fetch/cookie behavior. */
export function createWebTransport({ getDevUserId = () => null, isDevelopment = false, baseUrl = '' }: WebTransportOptions = {}): BorgeeTransport {
  return {
    async request({ path, method, headers: requestHeaders, body, responseType = 'json', signal }: ApiRequest): Promise<ApiResponse> {
      const headers = { ...(requestHeaders ?? {}) };
      const devUserId = getDevUserId();
      if (isDevelopment && devUserId) headers['X-Dev-User-Id'] = devUserId;

      let fetchBody: BodyInit | undefined;
      if (body?.kind === 'json') {
        fetchBody = body.value;
        if (!('Content-Type' in headers)) headers['Content-Type'] = 'application/json';
      } else if (body?.kind === 'multipart') {
        const form = new FormData();
        for (const part of body.parts) {
          if ('file' in part) {
            const bytes = part.file.bytes;
            const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
            form.append(part.name, new Blob([buffer], { type: part.file.contentType }), part.file.filename);
          } else {
            form.append(part.name, part.value);
          }
        }
        fetchBody = form;
      }

      const response = await fetch(`${baseUrl}${path}`, { method, headers, body: fetchBody, credentials: 'include', signal });
      const responseHeaders = Object.fromEntries(response.headers?.entries?.() ?? []);
      const base = { status: response.status, statusText: response.statusText, headers: responseHeaders };
      if (responseType === 'bytes') return { ...base, kind: 'bytes', bytes: new Uint8Array(await response.arrayBuffer()) };
      if (responseType === 'empty' || response.status === 204) return { ...base, kind: 'empty' };
      // Keep the former error diagnostic: a non-JSON response becomes an
      // error-shaped JSON payload whose message is the HTTP status text.
      const data = await response.json().catch(() => ({ error: response.statusText }));
      return { ...base, kind: 'json', data };
    },
  };
}
